import { useState, useEffect } from 'react'
import {
  Spinner, Alert, Form, Button, Card
} from 'react-bootstrap'
import client from '../../api/client'

function RoutingPreferences() {
  const [preferences, setPreferences] = useState({
    routing_strategy: 'auto',
    preferred_models: [],
    excluded_models: []
  })
  const [models, setModels] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingModels, setLoadingModels] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    fetchPreferences()
    fetchModels()
  }, [])

  const fetchPreferences = async () => {
    try {
      const response = await client.get('/api/v1/preferences')
      setPreferences(response.data)
    } catch (err) {
      setError('Erreur lors du chargement des préférences')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const fetchModels = async () => {
    try {
      const response = await client.get('/api/v1/models')
      setModels(response.data.models || [])
    } catch (err) {
      console.error('Failed to fetch models:', err)
    } finally {
      setLoadingModels(false)
    }
  }

  const handleStrategyChange = (e) => {
    setPreferences({ ...preferences, routing_strategy: e.target.value })
  }

  const handleModelToggle = (modelCode, listType) => {
    const list = preferences[listType]
    if (list.includes(modelCode)) {
      setPreferences({
        ...preferences,
        [listType]: list.filter(code => code !== modelCode)
      })
    } else {
      setPreferences({
        ...preferences,
        [listType]: [...list, modelCode]
      })
    }
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    setSuccess('')

    try {
      await client.put('/api/v1/preferences', preferences)
      setSuccess('Préférences enregistrées avec succès')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  if (loading || loadingModels) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <div>
      <h2 className="mb-4">Préférences de Routage</h2>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSave}>
            <Form.Group className="mb-4">
              <Form.Label>Stratégie de Routage</Form.Label>
              <Form.Select
                value={preferences.routing_strategy}
                onChange={handleStrategyChange}
              >
                <option value="auto">Auto (défaut)</option>
                <option value="cost">Coût (moins cher)</option>
                <option value="speed">Vitesse (plus rapide)</option>
                <option value="quality">Qualité (meilleur modèle)</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Stratégie utilisée par le routeur intelligent pour sélectionner le modèle
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Modèles Préférés</Form.Label>
              <div className="d-flex flex-wrap gap-2 p-3 border rounded">
                {models.slice(0, 15).map(model => (
                  <Form.Check
                    key={model.code}
                    type="checkbox"
                    id={`preferred-${model.code}`}
                    label={model.name}
                    checked={preferences.preferred_models.includes(model.code)}
                    onChange={() => handleModelToggle(model.code, 'preferred_models')}
                  />
                ))}
              </div>
              <Form.Text className="text-muted">
                {preferences.preferred_models.length} modèle(s) préféré(s)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Modèles Exclus</Form.Label>
              <div className="d-flex flex-wrap gap-2 p-3 border rounded">
                {models.slice(0, 15).map(model => (
                  <Form.Check
                    key={model.code}
                    type="checkbox"
                    id={`excluded-${model.code}`}
                    label={model.name}
                    checked={preferences.excluded_models.includes(model.code)}
                    onChange={() => handleModelToggle(model.code, 'excluded_models')}
                  />
                ))}
              </div>
              <Form.Text className="text-muted">
                {preferences.excluded_models.length} modèle(s) exclu(s)
              </Form.Text>
            </Form.Group>

            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? <Spinner as="span" animation="border" size="sm" /> : 'Enregistrer'}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  )
}

export default RoutingPreferences
